import { supabase } from "../../supabase";
import {
  applyOrganizationScope,
  orgScopedInsert,
  orgScopedUpdateByPk,
  resolveOrganizationIdForQueries,
} from "../orgQueryScope";

export function tradesToDropdownOptions(trades, { includeEmpty = true } = {}) {
  const options = (trades || []).map((trade) => ({
    value: trade.trade_id,
    label: trade.trade_name,
  }));

  if (includeEmpty) {
    return [{ value: "", label: "Select Trade" }, ...options];
  }

  return options;
}

/**
 * List organization trades. Fail-closed via org scope when actor has no org.
 * @param {object|null} actor
 * @param {{ activeOnly?: boolean }} [options]
 * @param {object} [client]
 */
export async function listTrades(
  actor = null,
  { activeOnly = true } = {},
  client = supabase,
) {
  const organizationId = await resolveOrganizationIdForQueries(actor, client);

  let query = client
    .from("trades")
    .select("trade_id, trade_name, is_active, organization_id, created_at, updated_at")
    .order("trade_name");

  query = applyOrganizationScope(query, organizationId, { tableName: "trades" });

  if (activeOnly) {
    query = query.eq("is_active", true);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data || [];
}

export async function listActiveTrades(actor = null, client = supabase) {
  return listTrades(actor, { activeOnly: true }, client);
}

export async function addTrade(tradeName, client = supabase, actor = null) {
  const name = String(tradeName || "").trim();
  if (!name) {
    throw new Error("Enter a trade name.");
  }

  const { data, error } = await orgScopedInsert(
    client,
    "trades",
    [{ trade_name: name, is_active: true }],
    actor,
    {
      select:
        "trade_id, trade_name, is_active, organization_id, created_at, updated_at",
      single: true,
    },
  );

  if (error) {
    throw error;
  }

  return data;
}

async function updateTradeRow(tradeId, patch, actor, client = supabase) {
  if (!actor) {
    throw new Error("Signed-in administrator required.");
  }

  if (!tradeId) {
    throw new Error("Select a trade.");
  }

  const builder = await orgScopedUpdateByPk(client, "trades", patch, actor, {
    pkColumn: "trade_id",
    pkValue: tradeId,
  });

  if (builder?.error) {
    throw builder.error;
  }

  const { data, error } = await builder
    .select(
      "trade_id, trade_name, is_active, organization_id, created_at, updated_at",
    )
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error("Trade not found in your organization.");
  }

  return data;
}

export async function updateTradeName(tradeId, tradeName, actor, client = supabase) {
  const name = String(tradeName || "").trim();
  if (!name) {
    throw new Error("Enter a trade name.");
  }

  return updateTradeRow(tradeId, { trade_name: name }, actor, client);
}

export async function deactivateTrade(tradeId, actor, client = supabase) {
  return updateTradeRow(tradeId, { is_active: false }, actor, client);
}

export async function activateTrade(tradeId, actor, client = supabase) {
  return updateTradeRow(tradeId, { is_active: true }, actor, client);
}
